import React, { useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { isValidEmail, isHoneypotTripped } from "@/utils/leadValidation";
import { trackEvent } from "@/utils/analytics";
import "./NewsletterSignup.scss";

type Status = "idle" | "submitting" | "success" | "error";

export interface NewsletterSignupProps {
  variant?: "inline" | "card";
}

const NewsletterSignup: React.FC<NewsletterSignupProps> = ({ variant = "card" }) => {
  const { t, i18n } = useTranslation();
  const inputId = useId();
  const [email, setEmail] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const successRef = useRef<HTMLParagraphElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    trackEvent("newsletter_submit");

    if (isHoneypotTripped(honeypot)) {
      setStatus("success");
      return;
    }

    if (!isValidEmail(email)) {
      setStatus("error");
      setErrorMessage(t("newsletter.error"));
      return;
    }

    setStatus("submitting");
    setErrorMessage("");

    try {
      const res = await fetch("/api/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, locale: i18n.language, company_url: honeypot }),
      });

      if (res.ok) {
        setStatus("success");
        trackEvent("newsletter_result", { status: "success" });
        setTimeout(() => successRef.current?.focus(), 50);
      } else {
        const data = await res.json().catch(() => ({}));
        setStatus("error");
        setErrorMessage(data.error ?? t("newsletter.error"));
        trackEvent("newsletter_result", { status: "error" });
      }
    } catch {
      setStatus("error");
      setErrorMessage(t("newsletter.error"));
      trackEvent("newsletter_result", { status: "error" });
    }
  };

  const cls = `newsletter-signup newsletter-signup--${variant}`;

  if (status === "success") {
    return (
      <div className={cls} role="status">
        <p ref={successRef} tabIndex={-1} className="newsletter-signup__success">
          {t("newsletter.success")}
        </p>
      </div>
    );
  }

  return (
    <div className={cls}>
      {variant === "card" && (
        <div className="newsletter-signup__header">
          <h2 className="newsletter-signup__heading">{t("newsletter.heading")}</h2>
          <p className="newsletter-signup__subline">{t("newsletter.subline")}</p>
        </div>
      )}
      {variant === "inline" && (
        <p className="newsletter-signup__inline-label">{t("newsletter.heading")}</p>
      )}

      <form onSubmit={handleSubmit} noValidate className="newsletter-signup__form">
        {/* Visually-hidden honeypot */}
        <label className="newsletter-signup__honeypot" aria-hidden="true">
          <span>Leave this blank</span>
          <input
            type="text"
            name="company_url"
            tabIndex={-1}
            autoComplete="off"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
          />
        </label>

        <label htmlFor={inputId} className="newsletter-signup__label">
          {t("newsletter.heading")}
        </label>
        <div className="newsletter-signup__row">
          <input
            id={inputId}
            type="email"
            name="email"
            className="newsletter-signup__input"
            placeholder={t("newsletter.placeholder")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={status === "submitting"}
            required
            aria-describedby={status === "error" ? `${inputId}-error` : undefined}
          />
          <button
            type="submit"
            className="newsletter-signup__btn"
            disabled={status === "submitting"}
          >
            {status === "submitting" ? t("newsletter.submitting") : t("newsletter.button")}
          </button>
        </div>

        <div
          role="status"
          aria-live="polite"
          className="newsletter-signup__status"
        >
          {status === "error" && (
            <span id={`${inputId}-error`} className="newsletter-signup__error">
              {errorMessage || t("newsletter.error")}
            </span>
          )}
        </div>
      </form>
    </div>
  );
};

export default NewsletterSignup;
